import { z } from 'zod';

/** Outgoing mail configured in the product (ADR 0016): the platform level (console) and, if
 *  it wants, each organization's own. The password is never sent back: `hasPassword` says
 *  whether one is set. The last delivery is shown by category, never the server's message. */
export const SMTP_SECURITY = ['tls', 'starttls', 'none'] as const;
export const smtpSecuritySchema = z.enum(SMTP_SECURITY);
export const SMTP_ERRORS = ['auth', 'connection', 'rejected', 'unknown'] as const;
export const smtpErrorSchema = z.enum(SMTP_ERRORS);
export type SmtpError = z.infer<typeof smtpErrorSchema>;

export const smtpSettingsSchema = z.object({
  configured: z.boolean(),
  host: z.string().nullable(),
  port: z.number().int().nullable(),
  security: smtpSecuritySchema.nullable(),
  username: z.string().nullable(),
  hasPassword: z.boolean(),
  sender: z.string().nullable(),
  enabled: z.boolean(),
  lastAttemptAt: z.string().datetime().nullable(),
  lastOutcome: z.enum(['ok', 'failed']).nullable(),
  lastError: smtpErrorSchema.nullable(),
});
export type SmtpSettings = z.infer<typeof smtpSettingsSchema>;

/** A sender is an address, optionally with a name: `Protezione Civile <avvisi@ente.it>`. */
const senderSchema = z
  .string()
  .trim()
  .max(255)
  .regex(/^(?:[^<>]{1,120} <)?[^\s@<>]+@[^\s@<>]+\.[^\s@<>]+>?$/);

export const smtpSettingsUpdateSchema = z.object({
  host: z.string().trim().min(1).max(255),
  port: z.number().int().min(1).max(65535),
  security: smtpSecuritySchema,
  username: z.string().trim().max(255).nullable(),
  /** Omitted: the stored password stays. `null`: no password. */
  password: z.string().min(1).max(512).nullable().optional(),
  sender: senderSchema,
  enabled: z.boolean(),
});
export type SmtpSettingsUpdate = z.infer<typeof smtpSettingsUpdateSchema>;

export const smtpTestSchema = z.object({ to: z.string().trim().toLowerCase().email() });
export const smtpTestResultSchema = z.object({
  outcome: z.enum(['ok', 'failed']),
  error: smtpErrorSchema.nullable(),
});
export type SmtpTestResult = z.infer<typeof smtpTestResultSchema>;
